import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { observer, inject } from 'mobx-react';
import { useNavigate } from 'react-router-dom';

import { useForm } from 'react-hook-form';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  HelpCircleIcon,
  IconButton,
  OrganizationAutocomplete,
  ProviderAutocomplete,
  TextField,
  Tooltip,
  Typography,
} from '@availity/element';

import ApplicationDescription from '../../Common/components/ApplicationDescription/ApplicationDescription';

import ProviderStore from '../../Common/stores/ProviderStore';
import CommonStore from '../../Common/stores/CommonStore';

const Provider = ({ commonStore, providerStore }) => {
  const { selectedOrganization, taxIds, taxId, customerId, npi } = providerStore;
  const { handleChangeCommonStore } = commonStore;
  const navigate = useNavigate();

  const form = useForm({
    defaultValues: {
      organizations: selectedOrganization,
      taxId,
      taxIds,
      npi,
      selectedProvider: providerStore.selectedProvider || null,
    },
  });

  const { register, formState, setValue, clearErrors, trigger, handleSubmit, watch } = form;
  const { errors } = formState;

  const [providerInput, setProviderInput] = useState('');

  useEffect(() => {
    handleChangeCommonStore('activePage', 'landing');
    handleChangeCommonStore('crumb', []);
  }, [handleChangeCommonStore]);

  const onSubmit = () => {
    navigate('/wizard', { replace: true });
  };

  const selectedProvider = watch('selectedProvider');

  useEffect(() => {
    if (selectedProvider) {
      const npi = selectedProvider.npi || '';
      setValue('npi', npi);
      clearErrors('npi');
      trigger('npi');
    }
  }, [selectedProvider, setValue, clearErrors, trigger]);

  useEffect(() => {
    if (taxId) {
      setValue('taxId', taxId ? { id: taxId, type: taxId } : null);
      trigger('taxId');
    }
  }, [taxId, setValue, clearErrors, trigger]);

  const onSelectedOrganization = (value) => {
    providerStore.onSelectedOrganization(value);
    setValue('taxId', null);
    setValue('selectedProvider', null);
  };

  const onSelectedTaxId = (value) => {
    const { setTaxId, setProvider } = providerStore;
    setTaxId(value);
    setProvider('');
  };

  const onSelectedProvider = (provider) => {
    const { setProvider } = providerStore;
    setProvider(provider);
  };

  const onNpiChangeHandler = (event) => {
    const { value } = event.target;
    const { setNPI } = providerStore;

    setNPI(value);
  };

  return (
    <>
      <ApplicationDescription />
      <form onSubmit={handleSubmit(onSubmit)} id="provider-form">
        <Typography variant="body2" sx={{ marginBottom: 2 }} id="required-fields">
          *required fields
        </Typography>
        <Grid container spacing={3} id="grid-org-taxid">
          <Grid size={8} id="grid-org">
            <OrganizationAutocomplete
              id="organizations-autocomplete"
              name="organizations"
              {...register('organizations', { required: 'Organization is required.' })}
              FieldProps={{
                label: (
                  <Box id="label-org">
                    Organization*
                    <Tooltip
                      id="organization-tooltip"
                      title="Select the Organization that applies to the registered Tax ID."
                      placement="right"
                    >
                      <IconButton style={{ padding: 0, marginLeft: 8 }} tabIndex={-1} id="icon-org-tooltip">
                        <HelpCircleIcon fontSize="xxsmall" id="icon-help-org" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ),
                placeholder: 'Select an Organization',
                error: !!errors.organizations?.message,
                helperText: errors.organizations?.message,
              }}
              onBlur={() => {
                trigger('organizations');
              }}
              value={selectedOrganization || null}
              onChange={(event, value) => {
                setValue('organizations', value);
                clearErrors('organizations');
                onSelectedOrganization(value);
                setValue('taxId', null);
                setValue('selectedProvider', null);
                clearErrors();
                // clear ProviderAutocomplete's query text ("q")
                setProviderInput(''); // <--- this ensures API is called fresh for new org
              }}
            />
          </Grid>

          <Grid size={4} id="grid-taxid">
            <Autocomplete
              {...register('taxId', { required: 'Tax ID is required.' })}
              id="taxid-autocomplete"
              name="taxId"
              loading={taxIds.length === 0}
              filterSelectedOptions={false}
              readOnly={!selectedOrganization.id}
              value={taxId ? { id: taxId, type: taxId } : null}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              onBlur={() => {
                trigger('taxId');
              }}
              onChange={(event, value) => {
                setValue('taxId', value);
                clearErrors('taxId');
                onSelectedTaxId(value?.id || '');
                setValue('selectedProvider', null);
                // clear ProviderAutocomplete's query text ("q")
                setProviderInput(''); // <--- this ensures API is called fresh for new org
              }}
              options={taxIds}
              getOptionLabel={(option) => option.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  id="textfield-taxid"
                  label={
                    <Box id="label-taxid">
                      Tax ID*
                      <Tooltip
                        id="taxId-tooltip"
                        title="Tax ID is required for this payer. Select the Tax ID that applies to your inquiry. If the Tax ID is not
                            listed you must register that Tax ID to your Organization on Availity."
                        placement="right"
                      >
                        <IconButton
                          style={{ padding: 0, marginLeft: 8 }}
                          tabIndex={-1}
                          aria-hidden="true"
                          id="icon-taxid-tooltip"
                        >
                          <HelpCircleIcon fontSize="xxsmall" id="icon-help-taxid" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                  variant="outlined"
                  placeholder="Select a Tax ID"
                  error={!!errors.taxId}
                  helperText={errors.taxId?.message}
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container spacing={3} id="grid-provider">
          <Grid size={8} id="grid-provider-autocomplete">
            <ProviderAutocomplete
              key={`${customerId || 'no-org'}-${providerStore.taxId || 'no-taxid'}`}
              queryKey={['prov-autocomplete', customerId, providerStore.taxId]}
              id="provider-autocomplete"
              name="selectedProvider"
              {...register('selectedProvider', { required: 'Select a Provider is required.' })}
              customerId={customerId}
              readOnly={!customerId}
              filterSelectedOptions={false}
              value={selectedProvider || null}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              queryOptions={{ enabled: !!customerId || !!providerStore.taxId }}
              inputValue={providerInput}
              onInputChange={(_, v) => setProviderInput(v ?? '')}
              filterOptions={(options, state) => {
                const raw = state.inputValue ?? '';
                const input = raw.trim();
                if (input.length === 0) return options;
                const hasLetters = /[A-Za-z]/.test(input);
                const isNumeric = /^\d+$/.test(input);
                const lower = input.toLowerCase();
                return options.filter((option) => {
                  const inCustomer = Array.isArray(option.customerIds) ? option.customerIds.includes(customerId) : true;
                  if (!inCustomer) return false;
                  const byName =
                    (option.uiDisplayName?.toLowerCase().includes(lower) ?? false) ||
                    (option.businessName?.toLowerCase().includes(lower) ?? false);

                  const byId = (option.taxId?.includes(input) ?? false) || (option.npi?.includes(input) ?? false) || 
                  (option.associatedTaxIds?.some(id => id.includes(input)) ?? false);

                  if (hasLetters && isNumeric) return byName || byId;
                  if (hasLetters) return byName;
                  if (isNumeric) return byId;
                  return false;
                });
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.uiDisplayName || option.businessName}
                </li>
              )}
              onBlur={() => {
                trigger('selectedProvider');
              }}
              onChange={(event, value) => {
                setValue('selectedProvider', value);
                clearErrors('selectedProvider');
                onSelectedProvider(value);
              }}
              FieldProps={{
                label: (
                  <Box id="label-provider">
                    Select a Provider*
                    <Tooltip
                      id="selectedProvider-tooltip"
                      aria-label="Select a Provider"
                      title="Select a Provider field (previously called Express Entry) allows you to select providers
                    that have been added to your
                    organization's Express Entry feature within Availity. For additional information,
                    please refer to Availity's help topic."
                      placement="right"
                    >
                      <IconButton
                        style={{ padding: 0, marginLeft: 8 }}
                        tabIndex={-1}
                        aria-hidden="true"
                        id="icon-provider-tooltip"
                      >
                        <HelpCircleIcon fontSize="xxsmall" id="icon-help-provider" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ),
                placeholder: 'Select a Provider',
                error: !!errors.selectedProvider?.message,
                helperText: errors.selectedProvider?.message,
              }}
            />
          </Grid>
          <Grid size={4} id="grid-npi-textfield">
            <TextField
              id="npi"
              name="npi"
              label="NPI"
              placeholder="Enter NPI"
              value={npi || ''}
              {...register('npi', {
                pattern: {
                  value: /^\d{10}$/,
                  message: 'Please enter a valid NPI.',
                },
                maxLength: 10,
              })}
              error={!!errors.npi}
              helperText={errors.npi?.message}
              inputRef={(input) => {
                if (input) {
                  input.maxLength = 10;
                }
              }}
              onBlur={() => {
                trigger('npi');
              }}
              onChange={(event) => {
                setValue('npi', event.target.value);
                clearErrors('npi');
                trigger('npi');
                onNpiChangeHandler(event);
              }}
              variant="outlined"
              fullWidth
            />
          </Grid>
        </Grid>
        <Grid container justifyContent="flex-end" spacing={2} id="grid-next-button-container">
          <Grid id="grid-next-button">
            <Button type="submit" variant="contained" color="primary" id="next-button">
              Next
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export default inject('commonStore', 'providerStore')(observer(Provider));

Provider.propTypes = {
  providerStore: PropTypes.oneOf([ProviderStore]).isRequired,
  commonStore: PropTypes.oneOf([CommonStore]).isRequired,
};
